/**
 * Tracks recently viewed products in localStorage.
 * Hardened: corrupt, blocked or full storage can never throw or break a page.
 */
export class RecentlyViewed {
  static #STORAGE_KEY = 'viewedProducts';
  static #MAX_PRODUCTS = 4;
  /** In-memory copy, used when storage is unavailable. @type {string[]} */
  static #memory = [];

  /**
   * Keeps only unique numeric ids, newest first, capped at the maximum.
   * @param {unknown} value
   * @returns {string[]}
   */
  static #sanitize(value) {
    if (!Array.isArray(value)) return [];
    /** @type {string[]} */
    const result = [];
    for (const item of value) {
      const id = String(item ?? '').trim();
      if (/^[0-9]+$/.test(id) && !result.includes(id)) result.push(id);
      if (result.length >= RecentlyViewed.#MAX_PRODUCTS) break;
    }
    return result;
  }

  /**
   * Adds a product to the recently viewed products list.
   * @param {string} productId - The ID of the product to add.
   */
  static addProduct(productId) {
    const id = String(productId ?? '').trim();
    if (!/^[0-9]+$/.test(id)) return;

    const viewed = RecentlyViewed.getProducts().filter((existing) => existing !== id);
    viewed.unshift(id);
    const next = viewed.slice(0, RecentlyViewed.#MAX_PRODUCTS);

    RecentlyViewed.#memory = next;
    try {
      localStorage.setItem(RecentlyViewed.#STORAGE_KEY, JSON.stringify(next));
    } catch (_) {
      // Storage blocked or full: keep the in-memory copy only.
    }
  }

  static clearProducts() {
    RecentlyViewed.#memory = [];
    try {
      localStorage.removeItem(RecentlyViewed.#STORAGE_KEY);
    } catch (_) {
      // no-op
    }
  }

  /**
   * Retrieves the list of recently viewed products.
   * @returns {string[]} The list of viewed products.
   */
  static getProducts() {
    try {
      const raw = localStorage.getItem(RecentlyViewed.#STORAGE_KEY);
      if (raw === null) return [];

      const parsed = JSON.parse(raw);
      const clean = RecentlyViewed.#sanitize(parsed);

      if (!Array.isArray(parsed) || clean.length !== parsed.length) {
        // Repair bad data in place instead of failing on every page.
        if (clean.length) localStorage.setItem(RecentlyViewed.#STORAGE_KEY, JSON.stringify(clean));
        else localStorage.removeItem(RecentlyViewed.#STORAGE_KEY);
      }

      RecentlyViewed.#memory = clean;
      return [...clean];
    } catch (_) {
      try {
        localStorage.removeItem(RecentlyViewed.#STORAGE_KEY);
      } catch (__) {
        // no-op
      }
      return [...RecentlyViewed.#memory];
    }
  }
}
